<script lang="ts">
  // Global layout — mounts the impersonation banner so an admin in an active
  // impersonation session always sees the persistent "Stop impersonating"
  // affordance, plus the persistent Footer below the page slot so the
  // Terms / Privacy / Pricing links Stripe Live mode requires are present
  // on every route.

  // Global Tailwind v4 entry + design-system tokens. Imported once here so the
  // tokens, self-hosted fonts, and base typography apply on every route
  // (wiki/feature-design-system.md, AC1/AC2/AC3).
  import '../app.css';

  import { onDestroy, onMount, untrack } from 'svelte';
  import { page } from '$app/stores';
  import { onNavigate } from '$app/navigation';
  import { updated } from '$app/state';
  import PracticeNav from '$lib/ui/nav/PracticeNav.svelte';
  import { Markdown, Notice } from '$lib/ui';
  import { t } from '$lib/i18n';
  import { UiKeys } from '@voicedojo/backend-types';
  import Footer from '$lib/ui/nav/Footer.svelte';
  import NameDialog from '$lib/ui/nav/NameDialog.svelte';
  import OfflineOfferDialog from '$lib/ui/offline/OfflineOfferDialog.svelte';
  import NewVersionDialog from '$lib/ui/nav/NewVersionDialog.svelte';
  import ImpersonationBanner from '$lib/ui/nav/ImpersonationBanner.svelte';
  import AccountMoveBanner from '$lib/ui/nav/AccountMoveBanner.svelte';
  import AccountSetupBanner from '$lib/ui/nav/AccountSetupBanner.svelte';
  import Toaster from '$lib/ui/nav/Toaster.svelte';
  import BackgroundUploads from '$lib/ui/nav/BackgroundUploads.svelte';
  import { takeUploads } from '$lib/stores/takeUploads.svelte';
  import { resumePendingUploads } from '$lib/api/uploads';
  import { installOverlayScrollbars } from '$lib/ui/scroll/overlayScrollbars';
  import { loadFeatureFlags } from '$lib/api/featureFlags';
  import { backendHealth } from '$lib/api/backendHealth';
  import { cellSwitchPending, reprobeCell } from '$lib/api/cellProbe';
  import { onBackendRecover } from '$lib/api/backendRecovery';
  import {
    auth,
    refresh as refreshAuth,
    bootstrapAuth,
    hasSessionIndicatorCookie,
    setCellCrossingHold,
    initAuthBroadcast,
    showsDeviceTakes,
  } from '$lib/stores/auth';
  import { currentUserLive } from '$lib/stores/currentUserLive.svelte';
  import { creditBalance } from '$lib/stores/creditBalance.svelte';
  import { quotaStore } from '$lib/stores/quotaStore';
  import { videoQualityStore } from '$lib/stores/videoQualityStore';
  import { focusStore } from '$lib/stores/focusStore';
  import { isPracticeRoute, isBareRoute, isAdminRoute, isAdminHost } from '$lib/utils/routing/navRoutes';
  import { reloadOnStaleChunk } from '$lib/utils/routing/staleDeploy';

  // App-wide backend-reachability signal (wiki/feature-offline-fallback-cues.md
  // §1/§2, OFC1/OFC2). The store is the single source of truth for the offline
  // banner below + the practice pages' fallback-cue / offline-music decisions.
  const health = backendHealth.store;

  // PracticeNav is the SINGLE global main nav (wiki/feature-global-nav-and-profile.md
  // §1); `navRoutes.ts` documents which routes it skips and why. `inTake` is the
  // ONE notion of "a take is mid-flight" — the route check is what stops a stale
  // focus flag stripping the chrome off a non-practice route (AC2).
  const focus = focusStore;
  const inTake = $derived(isPracticeRoute($page.url.pathname) && $focus.active);
  const showNav = $derived.by(() => {
    const path = $page.url.pathname;
    if (isBareRoute(path) || isAdminRoute(path)) return false;
    return !inTake;
  });

  // Boot-time feature-flag fetch (`GET /api/config/auth`). Memoised inside
  // `featureFlags.ts` so this single call services every consumer
  // (PracticeNav hides/shows the Active Listening link, the
  // `/active-listening` route gate, etc.). Fire-and-forget — call sites use
  // the cached synchronous accessor and re-render when this resolves.
  onMount(() => {
    // Resolve `whoami` once, globally (wiki/feature-global-nav-and-profile.md
    // AC3). This moved off the deleted AuthHeader, which only mounted on a
    // subset of routes — so the account dropdown now resolves on every route.
    // `bootstrapAuth()` is the SAME one-shot memo `hooks.client.ts` calls at
    // module-init, so whichever fires first, the initial whoami happens exactly
    // once (no staggered double-bootstrap). The `onBackendRecover` re-fire and
    // the cross-tab refetch below stay on `refresh()` — those are intentional
    // re-syncs that must hit the server afresh.
    void bootstrapAuth();
    void loadFeatureFlags();
    // Tear down on unmount so HMR / route teardown doesn't leak the probe timers.
    backendHealth.start();
    // Recover the stranded boot fetches when the backend comes back. If the
    // backend was down at boot, the whoami refresh leaves the auth store at the
    // Guest floor (the AccountMenu has already left its `loading` paint) and the
    // feature-flags fetch fails closed —
    // and neither re-runs on its own. On a genuine
    // offline→online edge of the health store we re-fire both so the nav
    // re-hydrates. `refresh()` in-flight-dedups and `loadFeatureFlags()`
    // re-fetches whenever it isn't cached/in-flight, so this is safe to call;
    // `onBackendRecover` never fires on the initial optimistic `online: true`.
    const stopRecover = onBackendRecover(health, () => {
      void refreshAuth();
      void loadFeatureFlags();
    });
    const stopReprobe = health.subscribe(({ online }) => {
      if (!online && !hasSessionIndicatorCookie()) void reprobeCell();
    });
    // Cross-tab session signal (wiki/feature-checkout-claim.md AC-CC-6): listen
    // for another tab establishing/claiming a session and refetch whoami so this
    // (e.g. the original guest) tab flips to authenticated live.
    const stopAuthBroadcast = initAuthBroadcast();
    const stopScrollbars = installOverlayScrollbars();
    const stopStaleChunk = reloadOnStaleChunk(() => inTake);
    setCellCrossingHold(() => inTake);
    return () => {
      stopStaleChunk();
      stopScrollbars();
      stopRecover();
      stopReprobe();
      stopAuthBroadcast();
      backendHealth.stop();
    };
  });

  // Read-channel watch on the signed-in user's own `User` read model, through the
  // SHARED `currentUserLive` store — the app's only `User` push handler
  // (wiki/feature-frontend-cqrs-dataflow.md §2 "Shared live store"). The shell
  // holds it for the whole session so a push refetches whoami ONCE, no matter how
  // many pages (/billing, /profile, /library) are reading the same model
  // at the time. A guest is never acquired, so it holds no connection.
  //
  // NOT acquired while impersonating, and any held acquisition is released on
  // entry: the hub groups on the AUTHENTICATED caller, which stays the admin, so
  // a push about the target would arrive for the wrong owner. An impersonation
  // session is bounded by a 15-minute token and the chrome stays correct by
  // explicit refetch (whoami on start, on stop, on the reload stop performs) —
  // a lagging balance inside it is acceptable, a push wired to the wrong owner
  // is not. See wiki/feature-impersonation.md AC12.5.
  //
  // `acquiredUserId` is deliberately NOT reactive: the store's own push refetch
  // rewrites `$auth`, which re-runs this effect, and acting on anything but a
  // genuine id change would release-and-reacquire on every refetch — a teardown
  // loop feeding itself.
  let acquiredUserId: string | null = null;
  $effect(() => {
    const userId =
      $auth.status === 'authenticated' && !$auth.user.impersonation ? $auth.user.id : null;
    if (userId === acquiredUserId) return;
    if (acquiredUserId) currentUserLive.release();
    acquiredUserId = userId;
    if (userId) currentUserLive.acquire(userId);
  });

  onDestroy(() => {
    if (acquiredUserId) currentUserLive.release();
  });

  // A take whose upload a closed tab cut short finishes on the next visit, whatever page
  // it lands on (wiki/feature-upload-reliability.md AC-U3).
  let resumedFor: string | null = null;
  $effect(() => {
    const userId = $auth.status === 'authenticated' && showsDeviceTakes($auth) ? $auth.user.id : null;
    if (!userId || userId === resumedFor) return;
    resumedFor = userId;
    void resumePendingUploads(new Set());
  });

  // A navigation the page guards let through loads the new deployment, or the new
  // cell, in full; onNavigate, unlike beforeNavigate, never fires for a cancelled one.
  // Not while a take is still sending: the full load would cut its upload short.
  onNavigate(({ to }) => {
    if ((!updated.current && !cellSwitchPending()) || !to || takeUploads.sending) return;
    location.href = to.url.href;
    return new Promise<never>(() => {});
  });

  $effect(() => {
    const user = $auth.status === 'authenticated' ? $auth.user : null;
    untrack(() => creditBalance.bind(user?.id ?? null, !!user?.impersonation));
  });

  $effect(() => {
    const identity =
      $auth.status === 'loading' || isAdminHost($page.url.hostname)
        ? null
        : `${$auth.user?.id ?? 'guest'}:${!!$auth.user?.impersonation}`;
    untrack(() => quotaStore.bind(identity));
  });

  // Seed the recorder's video-quality store PREFERRED tier from the user's
  // server-side default recording quality (whoami) — the single source of truth
  // the picker resets to before every take (wiki/feature-recording-video-quality.md
  // §"Default = the user's preferred quality; reset before every take"). A guest
  // (no settings) passes null, which the store resolves to DEFAULT_QUALITY_TIER;
  // a per-take pick can still override it for that take, but the next take
  // resets to the preferred.
  $effect(() => {
    videoQualityStore.setPreferred(
      $auth.status === 'authenticated' ? $auth.user.defaultRecordingQuality : null,
    );
  });
</script>

<!--
  Offline warning banner (wiki/feature-offline-fallback-cues.md §2, OFC2).
  Shown only when the backend probe reports unreachable. Slim, persistent
  (reflects live state), and accessible (role=status / aria-live=polite).
  Hidden entirely when online so it adds no layout cost on the happy path.
-->
<!--
  Global name dialog (wiki/feature-name-dialog.md AC2). Mounted here
  so every authenticated page evaluates the trigger condition on entry; the
  component renders nothing while signed-out or while the user already has
  a name set, so this mount is effectively free for the common path.
-->
<NameDialog />
{#if !isAdminRoute($page.url.pathname) && !isAdminHost($page.url.hostname)}
  <OfflineOfferDialog {inTake} />
{/if}
<NewVersionDialog {inTake} />

<!--
  The app shell is the ONE place a viewport height is claimed: a column at least
  as tall as the viewport, with the page slot taking the slack, so the Footer
  lands on the viewport floor under a short page and immediately under the
  content of a tall one. A page's own container must therefore never set a
  viewport-height floor of its own — that stacks a second screen under the nav
  and strands the footer below hundreds of pixels of nothing.
-->
<div class="app-shell">
  {#if !$health.online}
    <Notice tone="warning" role="status" class="offline-banner no-print">
      <Markdown source={t(UiKeys.Layout.Offline)} inline />
    </Notice>
  {/if}

  <ImpersonationBanner />
  {#if !inTake}
    <AccountMoveBanner />
  {/if}

  <!--
    Global main nav (wiki/feature-global-nav-and-profile.md §1). PracticeNav is the
    single navigation surface app-wide — the Playground / Mastery groups plus the
    AccountMenu dropdown. Rendered here above the page slot on every route except
    focus mode (a practice take mid-flight) and the bare auth / legal pages. The
    standalone AuthHeader is gone; its whoami refresh moved to onMount and its
    Admin extraItems are computed inside AccountMenu.
  -->
  {#if showNav}
    <PracticeNav />
  {/if}

  <div class="page">
    {#if !inTake}
      <AccountSetupBanner />
    {/if}
    <slot />
  </div>

  <!-- Hidden in a take: its links are sub-24px targets in the take's
       accessibility tree, and scrolling into them carries the sticky stage off. -->
  {#if !inTake}
    <Footer />
  {/if}
</div>

<!--
  App-wide toast stack (wiki/feature-api-client.md). Mounted ONCE here in the
  root layout so an uncaught ApiError from any page surfaces as a red toast via
  the global unhandledrejection handler in hooks.client.ts. Not in the
  (practice) group — this is the app-wide layout.
-->
<Toaster />
{#if !inTake}
  <BackgroundUploads />
{/if}

<style lang="scss">
  :global(body) {
    margin: 0;
    overflow-x: hidden;
  }

  .app-shell {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    min-height: 100dvh;
  }

  /* Takes the slack so the Footer sits on the viewport floor; `flex-basis: auto`
     keeps a tall page at its own content height. No viewport floor of its own —
     that is what reserved a second screen under the nav. */
  .page {
    flex: 1 0 auto;
    min-height: 0;
    padding-inline: var(--page-gutter);
  }

  /* Offline warning banner — the Notice primitive carries the warning tone +
     a11y; here we only re-lay it out as a slim, full-width top banner (page
     layout, not visual styling of the primitive). */
  :global(.offline-banner) {
    width: 100%;
    box-sizing: border-box;
    border: 0;
    border-bottom: 1px solid var(--line);
    border-radius: 0;
    font-size: var(--text-body);
  }
</style>
